import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, User, Calendar } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { NEWS, WHATSAPP_URL } from "../lib/content";

function renderBlock(block, idx) {
  switch (block.type) {
    case "h2":
      return (
        <h2
          key={idx}
          className="font-sans text-2xl md:text-3xl font-semibold text-sca-black tracking-tight mt-12 mb-5"
        >
          {block.text}
        </h2>
      );
    case "p":
      return (
        <p key={idx} className="font-sans text-base md:text-lg leading-[1.75] text-sca-graphite/85 mb-5">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul key={idx} className="my-6 space-y-3">
          {block.items.map((it, i) => (
            <li
              key={i}
              className="relative pl-8 text-base md:text-lg leading-relaxed text-sca-graphite/85"
            >
              <span
                className="absolute left-0 top-3 w-3 h-px bg-sca-red"
                aria-hidden
              />
              {it}
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote
          key={idx}
          className="relative my-12 pl-8 border-l-2 border-sca-red"
        >
          <p className="sca-serif italic text-2xl md:text-3xl leading-snug text-sca-black tracking-tight">
            {block.text}
          </p>
        </blockquote>
      );
    default:
      return null;
  }
}

export default function NoticiaDetail() {
  const { slug } = useParams();
  const idx = NEWS.findIndex((n) => n.slug === slug);
  const article = NEWS[idx];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | SCA Construtora`;
    }
  }, [article]);

  if (!article) return <Navigate to="/noticias" replace />;

  const prev = NEWS[(idx + NEWS.length - 1) % NEWS.length];
  const next = NEWS[(idx + 1) % NEWS.length];
  const related = NEWS.filter((_, i) => i !== idx).slice(0, 3);

  return (
    <>
      {/* Hero */}
      <article>
        <header className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white" data-testid={`news-article-${slug}`}>
          <div className="sca-container max-w-5xl">
            <Reveal>
              <Link
                to="/noticias"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-sca-steel hover:text-sca-red transition-colors mb-10"
                data-testid="news-back-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Todas as notícias
              </Link>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-7 text-[11px] tracking-[0.22em] uppercase font-bold">
                <span className="text-sca-red">{article.tag}</span>
                <span className="text-sca-steel inline-flex items-center gap-2">
                  <Calendar className="w-3 h-3" /> {article.date}
                </span>
                <span className="text-sca-steel inline-flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {article.readingTime}
                </span>
                <span className="text-sca-steel inline-flex items-center gap-2">
                  <User className="w-3 h-3" /> {article.author}
                </span>
              </div>

              <h1 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tightest text-sca-black max-w-4xl">
                {article.title}
              </h1>
              <p className="sca-body mt-6 max-w-3xl">{article.excerpt}</p>
            </Reveal>
          </div>
        </header>

        {/* Featured image */}
        <Reveal>
          <div className="relative h-[55vh] md:h-[75vh] overflow-hidden bg-sca-mist">
            <img src={article.image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </Reveal>

        {/* Body */}
        <section className="py-20 md:py-28 bg-white">
          <div className="sca-container max-w-3xl">
            <Reveal>
              {article.body.map(renderBlock)}
            </Reveal>

            {/* Inline CTA */}
            <div className="mt-16 p-8 md:p-10 bg-sca-black text-white">
              <p className="sca-overline text-white/60 mb-4">[ Aplicar na sua obra ]</p>
              <h3 className="font-sans text-2xl md:text-3xl font-semibold leading-tight mb-6">
                Quer que a engenharia da SCA estruture sua próxima obra?
              </h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/contato" className="btn-sca-primary" data-testid="news-inline-cta">
                  Solicitar avaliação <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href={WHATSAPP_URL(`Quero falar sobre o artigo "${article.title}".`)} target="_blank" rel="noreferrer" className="btn-sca px-7 py-4 border border-white/25 text-white hover:bg-white hover:text-sca-black">
                  WhatsApp técnico
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Prev / Next */}
        <section className="border-t border-sca-line">
          <div className="sca-container grid grid-cols-1 md:grid-cols-2 gap-px bg-sca-line">
            <Link
              to={`/noticias/${prev.slug}`}
              className="group bg-white p-8 md:p-12 hover:bg-sca-black hover:text-white transition-colors duration-500"
              data-testid="news-prev-link"
            >
              <p className="sca-overline mb-4 text-sca-red">[ Anterior ]</p>
              <p className="font-sans text-xl md:text-2xl font-semibold leading-tight max-w-md">
                {prev.title}
              </p>
            </Link>
            <Link
              to={`/noticias/${next.slug}`}
              className="group bg-white p-8 md:p-12 md:text-right hover:bg-sca-black hover:text-white transition-colors duration-500"
              data-testid="news-next-link"
            >
              <p className="sca-overline mb-4 text-sca-red">[ Próximo ]</p>
              <p className="font-sans text-xl md:text-2xl font-semibold leading-tight max-w-md md:ml-auto">
                {next.title}
              </p>
            </Link>
          </div>
        </section>

        {/* Related */}
        <section className="sca-section bg-sca-mist">
          <div className="sca-container">
            <Reveal>
              <p className="sca-overline mb-6">[ Continue lendo ]</p>
              <h2 className="sca-h2 mb-12 max-w-3xl">
                Outros artigos do <span className="sca-serif text-sca-red">Conteúdo técnico SCA</span>.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((n) => (
                <Link
                  key={n.slug}
                  to={`/noticias/${n.slug}`}
                  data-testid={`related-news-${n.slug}`}
                  className="group block bg-white border border-sca-line hover:bg-sca-black hover:text-white transition-colors duration-500"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-sca-red mb-3">{n.tag} · {n.date}</p>
                    <h3 className="font-sans text-lg md:text-xl font-semibold leading-tight group-hover:text-white">{n.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
